// src/pages/lokasi/ClosingPlantation.jsx
import React, { useState, useEffect, useRef } from "react";
import {
    Typography,
    Box,
    Breadcrumbs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    Link as MuiLink,
    TablePagination,
    Chip,
    IconButton,
    MenuItem,
    Stack,
    Snackbar,
    FormControl,
    InputLabel,
    Select,
    Button
} from "@mui/material";
//import MoreVertIcon from "@mui/icons-material/MoreVert";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver"; // Import saveAs

import { useTheme } from "@mui/material/styles";
import useAuthCheck from "../../hooks/useAuthCheck";
import { useOutletContext, useNavigate } from "react-router-dom";
import { database } from "../../firebase";
import { ref, onValue } from "firebase/database"; // Removed push, update, set as import is not allowed

// Helper: ubah onValue menjadi Promise (untuk fetch data dari Firebase)
const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

// Helper function to convert Excel date number to YYYY-MM-DD string
const excelDateToJSDate = (excelDate) => {
    if (typeof excelDate === 'number' && excelDate > 0) {
        const date = new Date(Math.round((excelDate - 25569) * 86400 * 1000));
        return date.toISOString().split('T')[0];
    }
    return excelDate;
};

// Helper function to format a YYYY-MM-DD date string to DD-MM-YYYY
const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString || typeof dateString !== 'string') return 'N/A';
    const parts = dateString.split('-');
    if (parts.length === 3) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dateString;
};

// Styles for TableHead Cells
const headerCellStyle = {
    border: '1px solid rgba(224, 224, 224, 1)',
    position: 'sticky',
    backgroundColor: 'white',
    zIndex: 2
};

const subHeaderCellStyle = {
    border: '1px solid rgba(224, 224, 224, 1)',
    position: 'sticky',
    backgroundColor: 'white',
    zIndex: 1,
    top: 56
};


function ClosingPlantation() {
    const { user, loading: authLoading } = useAuthCheck();
    const theme = useTheme();
    const navigate = useNavigate();
    const { breadcrumbs } = useOutletContext();

    const [dataList, setDataList] = useState([]);
    const [originalDataList, setOriginalDataList] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });
    const [showFilterPanel, setShowFilterPanel] = useState(false);

    // State untuk data konfigurasi dropdown
    const [kodeWilayahList, setKodeWilayahList] = useState([]);
    const [kepalaWilayahList, setKepalaWilayahList] = useState([]);
    const [kepalaKebunList, setKepalaKebunList] = useState([]);
    const [statusLokasiList, setStatusLokasiList] = useState([]);
    const [varietasBibitList, setVarietasBibitList] = useState([]);
    const [jenisBibitList, setJenisBibitList] = useState([]);
    const [kelasBibitList, setKelasBibitList] = useState([]);
    const [helperData, setHelperData] = useState({}); // New state for db_config/db_helper data

    // Filter states
    const [filterStatusLokasi, setFilterStatusLokasi] = useState('');
    const [filterKepalaWilayah, setFilterKepalaWilayah] = useState('');
    const [filterKepalaKebun, setFilterKepalaKebun] = useState('');
    const [filterJenisBibit, setFilterJenisBibit] = useState('');
    const [filterKelasBibit, setFilterKelasBibit] = useState('');


    useEffect(() => {
        const fetchData = async () => {
            setLoadingData(true);
            try {
                // Fetch main data from db_freshpine/tb_lokasi
                const lokasiRef = ref(database, "db_freshpine/tb_lokasi");
                const lokasiSnapshot = await onValuePromise(lokasiRef);
                const lokasiData = lokasiSnapshot.val();
                const loadedLokasi = [];
                if (lokasiData) {
                    Object.keys(lokasiData).forEach(key => {
                        loadedLokasi.push({ firebaseKey: key, ...lokasiData[key] });
                    });
                }
                // Filter data to only show 'closing' status_plantation
                const closingData = loadedLokasi.filter(item => item.status_plantation === 'closing');
                setOriginalDataList(closingData);
                setDataList(closingData);

                // Fetch lookup data for Kode Wilayah
                const kodeWilayahConfigRef = ref(database, "db_config/db_fresh_pineapple/tb_kode_wilayah");
                const kodeWilayahConfigSnapshot = await onValuePromise(kodeWilayahConfigRef);
                const kodeWilayahConfigData = kodeWilayahConfigSnapshot.val();
                if (kodeWilayahConfigData) {
                    const list = Object.keys(kodeWilayahConfigData).map(key => ({
                        id: key,
                        nama_kode_wilayah: kodeWilayahConfigData[key].nama_kode_wilayah,
                        id_kepala_wilayah: kodeWilayahConfigData[key].id_kepala_wilayah || ''
                    }));
                    setKodeWilayahList(list);
                }

                // Mengaktifkan kembali fetch data untuk Kepala Wilayah
                const kepalaWilayahConfigRef = ref(database, "db_config/db_fresh_pineapple/tb_kepala_wilayah");
                const kepalaWilayahConfigSnapshot = await onValuePromise(kepalaWilayahConfigRef);
                const kepalaWilayahConfigData = kepalaWilayahConfigSnapshot.val();
                if (kepalaWilayahConfigData) {
                    const list = Object.keys(kepalaWilayahConfigData).map(key => ({
                        id: key,
                        nama_kepala_wilayah: kepalaWilayahConfigData[key].nama_kepala_wilayah
                    }));
                    setKepalaWilayahList(list);
                }

                // Fetch lookup data for Kepala Kebun
                const kepalaKebunConfigRef = ref(database, "db_config/db_fresh_pineapple/tb_kepala_kebun");
                const kepalaKebunConfigSnapshot = await onValuePromise(kepalaKebunConfigRef);
                const kepalaKebunConfigData = kepalaKebunConfigSnapshot.val();
                if (kepalaKebunConfigData) {
                    const list = Object.keys(kepalaKebunConfigData).map(key => ({
                        id: key,
                        nama_kepala_kebun: kepalaKebunConfigData[key].nama_kepala_kebun
                    }));
                    setKepalaKebunList(list);
                }

                // Fetch lookup data for Status Lokasi, now including 'indicator'
                const statusLokasiConfigRef = ref(database, "db_config/db_fresh_pineapple/tb_status_lokasi");
                const statusLokasiConfigSnapshot = await onValuePromise(statusLokasiConfigRef);
                const statusLokasiConfigData = statusLokasiConfigSnapshot.val();
                if (statusLokasiConfigData) {
                    const list = Object.keys(statusLokasiConfigData).map(key => ({
                        id: key,
                        nama_status_lokasi: statusLokasiConfigData[key].nama_status_lokasi,
                        indicator: statusLokasiConfigData[key].indicator || '#FFFFFF'
                    }));
                    setStatusLokasiList(list);
                }

                // Fetch lookup data for Varietas Bibit
                const varietasBibitConfigRef = ref(database, "db_config/db_fresh_pineapple/tb_varietas_bibit");
                const varietasBibitConfigSnapshot = await onValuePromise(varietasBibitConfigRef);
                const varietasBibitConfigData = varietasBibitConfigSnapshot.val();
                if (varietasBibitConfigData) {
                    const list = Object.keys(varietasBibitConfigData).map(key => ({
                        id: key,
                        nama_varietas_bibit: varietasBibitConfigData[key].nama_varietas_bibit
                    }));
                    setVarietasBibitList(list);
                }

                // Fetch lookup data for Jenis Bibit
                const jenisBibitConfigRef = ref(database, "db_config/db_fresh_pineapple/tb_jenis_bibit");
                const jenisBibitConfigSnapshot = await onValuePromise(jenisBibitConfigRef);
                const jenisBibitConfigData = jenisBibitConfigSnapshot.val();
                if (jenisBibitConfigData) {
                    const list = Object.keys(jenisBibitConfigData).map(key => ({
                        id: key,
                        nama_jenis_bibit: jenisBibitConfigData[key].nama_jenis_bibit
                    }));
                    setJenisBibitList(list);
                }

                // Fetch lookup data for Kelas Bibit
                const kelasBibitConfigRef = ref(database, "db_config/db_fresh_pineapple/tb_kelas_bibit");
                const kelasBibitConfigSnapshot = await onValuePromise(kelasBibitConfigRef);
                const kelasBibitConfigData = kelasBibitConfigSnapshot.val();
                if (kelasBibitConfigData) {
                    const list = Object.keys(kelasBibitConfigData).map(key => ({
                        id: key,
                        nama_kelas_bibit: kelasBibitConfigData[key].nama_kelas_bibit
                    }));
                    setKelasBibitList(list);
                }

                // Fetch data from db_config/db_helper
                const helperRef = ref(database, "db_config/db_helper/tb_wilayah_plant_care"); // Path to db_helper
                const helperSnapshot = await onValuePromise(helperRef);
                const helperRawData = helperSnapshot.val();
                if (helperRawData) {
                    setHelperData(helperRawData);
                } else {
                    setHelperData({});
                }


            } catch (error) {
                console.error("Error fetching data:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal memuat data.",
                    severity: "error",
                });
            } finally {
                setLoadingData(false);
            }
        };

        if (user) {
            fetchData();
        }
    }, [user]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleMenuClick = (event) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleExport = () => {
        if (!dataList || dataList.length === 0) {
            setSnackbar({
                open: true,
                message: "Tidak ada data untuk diekspor.",
                severity: "warning",
            });
            return;
        }

        const formattedData = dataList.map((item) => {
            const row = {};
            row["Lokasi"] = item.lokasi || 'N/A';
            row["Status"] = item.status || 'N/A';
            // Lookup nama_kepala_wilayah berdasarkan id_kepala_wilayah dari kodeWilayahList
            const kodeWilayahItem = kodeWilayahList.find(kw => kw.nama_kode_wilayah === item.wilayah);
            row["Kepala Wilayah"] = kodeWilayahItem?.id_kepala_wilayah
                ? kepalaWilayahList.find(kw => kw.id === kodeWilayahItem.id_kepala_wilayah)?.nama_kepala_wilayah || 'N/A'
                : 'N/A';
            // Lookup nama_kepala_kebun based on lokasi from helperData for export
            let exportKepalaKebun = 'N/A';
            if (helperData[item.lokasi] && helperData[item.lokasi].id_kepala_kebun) {
                const kepalaKebunIdFromHelper = helperData[item.lokasi].id_kepala_kebun;
                const kepalaKebunFound = kepalaKebunList.find(
                    (kk) => kk.id === kepalaKebunIdFromHelper
                );
                exportKepalaKebun = kepalaKebunFound?.nama_kepala_kebun || 'N/A';
            }
            row["Kepala Kebun"] = exportKepalaKebun;

            row["Kode Wilayah"] = kodeWilayahItem?.nama_kode_wilayah || item.wilayah || 'N/A';

            const bibitString = item.bibit || '';
            row["Varietas Bibit"] = bibitString.length >= 5 ? bibitString.substring(2, 5) : 'N/A';
            row["Jenis Bibit"] = bibitString.length >= 6 ? bibitString.substring(5, 6) : 'N/A';
            row["Kelas Bibit"] = bibitString.length >= 7 ? bibitString.substring(6, 7) : 'N/A';

            row["Luas Bruto"] = item.bruto || 'N/A';
            row["Luas Netto"] = item.netto || 'N/A';
            row["Luas Aktif"] = item.aktif || 'N/A';
            row["Tanggal Mulai Tanam"] = formatDateToDDMMYYYY(item.mulai_tanam);
            row["Tanggal Mulai Rawat"] = formatDateToDDMMYYYY(item.mulai_rawat);
            row["Tanggal Perubahan Status"] = formatDateToDDMMYYYY(item.perubahan_status);
            return row;
        });

        const worksheet = XLSX.utils.json_to_sheet(formattedData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "ClosingPlantation");

        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/octet-stream" });

        saveAs(blob, `Data_Closing_Plantation.xlsx`);
        setSnackbar({
            open: true,
            message: "Data berhasil diekspor ke Excel.",
            severity: "success",
        });
        handleMenuClose();
    };

    const handlePrint = () => {
        setSnackbar({
            open: true,
            message: "Fungsi Print akan dikembangkan.",
            severity: "info",
        });
        handleMenuClose();
    };

    const handleToggleFilterPanel = () => {
        setShowFilterPanel(prev => !prev);
    };

    const handleFilterData = () => {
        let filtered = [...originalDataList];

        if (filterStatusLokasi) {
            filtered = filtered.filter(item => item.status === filterStatusLokasi);
        }
        if (filterKepalaWilayah) {
            const selectedKepalaWilayahId = kepalaWilayahList.find(kw => kw.nama_kepala_wilayah === filterKepalaWilayah)?.id;
            if (selectedKepalaWilayahId) {
                filtered = filtered.filter(item => {
                    const kodeWilayahItem = kodeWilayahList.find(kw => kw.nama_kode_wilayah === item.wilayah);
                    return kodeWilayahItem?.id_kepala_wilayah === selectedKepalaWilayahId;
                });
            }
        }
        if (filterKepalaKebun) {
            // Filter based on the selectedKepalaKebun (name)
            filtered = filtered.filter(item => {
                const helperEntry = helperData[item.lokasi];
                if (helperEntry && helperEntry.id_kepala_kebun) {
                    const kepalaKebunName = kepalaKebunList.find(kk => kk.id === helperEntry.id_kepala_kebun)?.nama_kepala_kebun;
                    return kepalaKebunName === filterKepalaKebun;
                }
                return false;
            });
        }
        if (filterJenisBibit) {
            filtered = filtered.filter(item => {
                const bibitString = item.bibit || '';
                const jenis = bibitString.length >= 6 ? bibitString.substring(5, 6) : 'N/A';
                return jenis === filterJenisBibit;
            });
        }
        if (filterKelasBibit) {
            filtered = filtered.filter(item => {
                const bibitString = item.bibit || '';
                const kelas = bibitString.length >= 7 ? bibitString.substring(6, 7) : 'N/A';
                return kelas === filterKelasBibit;
            });
        }

        setDataList(filtered);
        setPage(0);
    };

    const handleResetFilter = () => {
        setFilterStatusLokasi('');
        setFilterKepalaWilayah('');
        setFilterKepalaKebun('');
        setFilterJenisBibit('');
        setFilterKelasBibit('');
        setDataList(originalDataList);
        setPage(0);
    };


    if (authLoading || loadingData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    Anda harus login untuk mengakses halaman ini
                </Alert>
            </Box>
        );
    }

    const displayedDataList = dataList;

    return (
        <Box sx={{ pb: 3 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                {breadcrumbs && breadcrumbs.map((crumb, index) => (
                    crumb.path ? (
                        <MuiLink
                            key={index}
                            underline="hover"
                            color="inherit"
                            onClick={() => navigate(crumb.path)}
                            sx={{ cursor: 'pointer' }}
                        >
                            {crumb.label}
                        </MuiLink>
                    ) : (
                        <Typography key={index} color="text.primary">
                            {crumb.label}
                        </Typography>
                    )
                ))}
            </Breadcrumbs>

            {showFilterPanel && (
                <Paper sx={{ width: '100%', overflow: 'hidden', p: 2, mb: 2 }}>
                    <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
                        Filter
                    </Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status Lokasi</InputLabel>
                            <Select
                                value={filterStatusLokasi}
                                label="Status Lokasi"
                                onChange={(e) => setFilterStatusLokasi(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {statusLokasiList.map((status) => (
                                    <MenuItem key={status.id} value={status.nama_status_lokasi}>
                                        {status.nama_status_lokasi}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small">
                            <InputLabel>Kepala Wilayah</InputLabel>
                            <Select
                                value={filterKepalaWilayah}
                                label="Kepala Wilayah"
                                onChange={(e) => setFilterKepalaWilayah(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {kepalaWilayahList.map((kw) => (
                                    <MenuItem key={kw.id} value={kw.nama_kepala_wilayah}>
                                        {kw.nama_kepala_wilayah}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small">
                            <InputLabel>Kepala Kebun</InputLabel>
                            <Select
                                value={filterKepalaKebun}
                                label="Kepala Kebun"
                                onChange={(e) => setFilterKepalaKebun(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {kepalaKebunList.map((kk) => (
                                    <MenuItem key={kk.id} value={kk.nama_kepala_kebun}>
                                        {kk.nama_kepala_kebun}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small">
                            <InputLabel>Jenis Bibit</InputLabel>
                            <Select
                                value={filterJenisBibit}
                                label="Jenis Bibit"
                                onChange={(e) => setFilterJenisBibit(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {jenisBibitList.map((jb) => (
                                    // Use charAt(0) for value to match filtering logic
                                    <MenuItem key={jb.id} value={jb.nama_jenis_bibit.charAt(0)}>
                                        {jb.nama_jenis_bibit}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small">
                            <InputLabel>Kelas Bibit</InputLabel>
                            <Select
                                value={filterKelasBibit}
                                label="Kelas Bibit"
                                onChange={(e) => setFilterKelasBibit(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {kelasBibitList.map((kb) => (
                                    // Use charAt(0) for value to match filtering logic
                                    <MenuItem key={kb.id} value={kb.nama_kelas_bibit.charAt(0)}>
                                        {kb.nama_kelas_bibit}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button variant="outlined" onClick={handleResetFilter}>
                            Reset Filter
                        </Button>
                        <Button variant="contained" onClick={handleFilterData}>
                            Filter Data
                        </Button>
                    </Stack>
                </Paper>
            )}

            <Paper sx={{ width: '100%', overflow: 'hidden', p: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="h6" component="h2">
                        Data Lokasi Closing
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <IconButton color="primary" size="small" onClick={handleToggleFilterPanel}>
                            {showFilterPanel ? <FilterAltOffOutlinedIcon /> : <FilterAltOutlinedIcon />}
                        </IconButton>
                        <IconButton color="primary" size="small" onClick={handleExport}>
                            <FileDownloadOutlinedIcon />
                        </IconButton>
                        <IconButton color="primary" size="small" onClick={handlePrint}>
                            <PrintOutlinedIcon />
                        </IconButton>
                    </Stack>
                </Stack>

                <TableContainer sx={{ maxHeight: 600, overflow: 'auto' }}>
                    <Table stickyHeader aria-label="closing plantation table">
                        <TableHead>
                            <TableRow>
                                <TableCell rowSpan={2} sx={headerCellStyle}>Lokasi</TableCell>
                                <TableCell rowSpan={2} sx={headerCellStyle}>Status</TableCell>
                                <TableCell rowSpan={2} sx={headerCellStyle}>Kepala Wilayah</TableCell>
                                <TableCell rowSpan={2} sx={headerCellStyle}>Kepala Kebun</TableCell>
                                <TableCell rowSpan={2} sx={headerCellStyle}>Kode Wilayah</TableCell>
                                <TableCell colSpan={3} align="center" sx={headerCellStyle}>BIBIT</TableCell>
                                <TableCell colSpan={3} align="center" sx={headerCellStyle}>LUAS</TableCell>
                                <TableCell colSpan={3} align="center" sx={headerCellStyle}>Tanggal</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={subHeaderCellStyle}>Varietas</TableCell>
                                <TableCell sx={subHeaderCellStyle}>Jenis</TableCell>
                                <TableCell sx={subHeaderCellStyle}>Kelas</TableCell>
                                <TableCell sx={subHeaderCellStyle}>Bruto</TableCell>
                                <TableCell sx={subHeaderCellStyle}>Netto</TableCell>
                                <TableCell sx={subHeaderCellStyle}>Aktif</TableCell>
                                <TableCell sx={subHeaderCellStyle}>Mulai Tanam</TableCell>
                                <TableCell sx={subHeaderCellStyle}>Mulai Rawat</TableCell>
                                <TableCell sx={subHeaderCellStyle}>Perubahan Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {displayedDataList.length > 0 ? (
                                displayedDataList
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((row) => {
                                        const kodeWilayahItem = kodeWilayahList.find(
                                            (item) => item.nama_kode_wilayah === row.wilayah
                                        );
                                        const displayKodeWilayah = kodeWilayahItem?.nama_kode_wilayah || row.wilayah || 'N/A';

                                        const displayKepalaWilayah = kodeWilayahItem?.id_kepala_wilayah
                                            ? kepalaWilayahList.find(
                                                (item) => item.id === kodeWilayahItem.id_kepala_wilayah
                                            )?.nama_kepala_wilayah || 'N/A'
                                            : 'N/A';

                                        // Lookup nama_kepala_kebun based on lokasi from helperData
                                        let displayKepalaKebun = 'N/A';
                                        if (helperData[row.lokasi] && helperData[row.lokasi].id_kepala_kebun) {
                                            const kepalaKebunIdFromHelper = helperData[row.lokasi].id_kepala_kebun;
                                            const kepalaKebunFound = kepalaKebunList.find(
                                                (item) => item.id === kepalaKebunIdFromHelper
                                            );
                                            displayKepalaKebun = kepalaKebunFound?.nama_kepala_kebun || 'N/A';
                                        }

                                        const statusLokasiItem = statusLokasiList.find(
                                            (item) => item.nama_status_lokasi === row.status
                                        );
                                        const displayStatusLokasi = statusLokasiItem?.nama_status_lokasi || row.status || 'N/A';
                                        const statusIndicatorColor = statusLokasiItem?.indicator || '#FFFFFF';

                                        const bibitString = row.bibit || '';
                                        let displayVarietas;
                                        let displayJenis;
                                        let displayKelas;

                                        if (bibitString.length === 0) {
                                            displayVarietas = 'MD2';
                                            displayJenis = 'Ratoon';
                                            displayKelas = 'Ratoon';
                                        } else {
                                            displayVarietas = bibitString.length >= 5 ? bibitString.substring(2, 5) : 'N/A';
                                            displayJenis = bibitString.length >= 6 ? bibitString.substring(5, 6) : 'N/A';
                                            displayKelas = bibitString.length >= 7 ? bibitString.substring(6, 7) : 'N/A';
                                        }

                                        return (
                                            <TableRow key={row.firebaseKey}>
                                                <TableCell>{row.lokasi || 'N/A'}</TableCell>
                                                <TableCell>
                                                    {displayStatusLokasi ? (
                                                        <Chip
                                                            label={displayStatusLokasi}
                                                            style={{ backgroundColor: statusIndicatorColor, color: theme.palette.getContrastText(statusIndicatorColor) }}
                                                            size="small"
                                                        />
                                                    ) : 'N/A'}
                                                </TableCell>
                                                <TableCell>{displayKepalaWilayah}</TableCell>
                                                <TableCell>{displayKepalaKebun}</TableCell> {/* Updated to use displayKepalaKebun */}
                                                <TableCell>{displayKodeWilayah}</TableCell>
                                                <TableCell>{displayVarietas}</TableCell>
                                                <TableCell>{displayJenis}</TableCell>
                                                <TableCell>{displayKelas}</TableCell>
                                                <TableCell>{row.bruto || 'N/A'}</TableCell>
                                                <TableCell>{row.netto || 'N/A'}</TableCell>
                                                <TableCell>{row.aktif || 'N/A'}</TableCell>
                                                <TableCell>{formatDateToDDMMYYYY(row.mulai_tanam)}</TableCell>
                                                <TableCell>{formatDateToDDMMYYYY(row.mulai_rawat)}</TableCell>
                                                <TableCell>{formatDateToDDMMYYYY(row.perubahan_status)}</TableCell>
                                                
                                            </TableRow>
                                        );
                                    })
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={15} align="center">
                                        Tidak ada data lokasi closing.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={displayedDataList.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default ClosingPlantation;
